(() => {
  if (window.WolfRecommendationRanking) return;

  const list = (value) => Array.isArray(value) ? value : [];
  const normalize = (value) => typeof value === 'string' ? value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase() : '';
  const id = (value) => typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  const ids = (value) => new Set(list(value).map(id).filter(Boolean));
  const tags = (product) => Array.isArray(product?.tags) ? product.tags : typeof product?.tags === 'string' ? product.tags.split(',') : [];
  const formPatterns = [
    ['Mugs', /\bmugs?\b/i], ['Cups', /\bcups?\b|\btumblers?\b/i],
    ['Plates', /\bplates?\b/i], ['Bowls', /\bbowls?\b/i],
    ['Servingware', /\bservingware\b|\bserving\b|\bplatters?\b|\bpitchers?\b/i],
    ['Sets', /\bsets?\b|\bbundles?\b|\bplace settings?\b/i],
    ['Vases', /\bvases?\b/i], ['Planters', /\bplanters?\b/i],
    ['Accessories', /\baccessor(?:y|ies)\b|\bspoons?\b|\bcoasters?\b|\bspoon rests?\b/i],
  ];
  const defaults = {
    Mugs: ['Mugs', 'Cups', 'Accessories'], Cups: ['Cups', 'Mugs', 'Accessories'],
    Plates: ['Plates', 'Bowls', 'Servingware', 'Sets'], Bowls: ['Bowls', 'Plates', 'Servingware'],
    Servingware: ['Servingware', 'Bowls', 'Plates'], Sets: ['Sets', 'Plates', 'Bowls', 'Servingware'],
    Vases: ['Vases', 'Planters'], Planters: ['Planters', 'Vases'], Accessories: ['Accessories', 'Mugs', 'Cups'],
  };

  function forms(product) {
    if (!product || typeof product !== 'object') return [];
    const values = [product.type, ...tags(product), product.title];
    return formPatterns.filter(([, pattern]) => values.some((value) => typeof value === 'string' && pattern.test(value))).map(([name]) => name);
  }

  function canonicalForms(values) {
    return [...new Set(list(values).flatMap((value) => forms({ type: value })))];
  }

  function family(product) {
    return normalize(id(product?.familyKey)) || `product:${id(product?.id)}`;
  }

  function eligibleVariants(product, settings) {
    const productTags = tags(product).map(normalize);
    const description = [product.type, product.title, ...productTags].filter((value) => typeof value === 'string').join(' ');
    if (product.excluded === true || productTags.includes('recommendation-exclude') ||
        product.giftCard === true || /\bgift[ -]?cards?\b/i.test(description) ||
        (!settings.includeMadeToOrder && (product.madeToOrder === true || /\bmade[ -]to[ -]order\b|\bmto\b/i.test(description)))) return [];
    return list(product.variants).filter((variant) => variant && id(variant.id) && variant.available === true &&
      (settings.includeMadeToOrder || variant.madeToOrder !== true) &&
      typeof variant.image === 'string' && variant.image.trim() && Number.isFinite(Number(variant.price)) && Number(variant.price) > 0);
  }

  function affinities(history, currentId) {
    const formScores = new Map();
    const colorScores = new Map();
    const seen = new Set();
    // Position-based decay keeps the ranking deterministic. The caller expires
    // stored history; only the twenty newest distinct, non-current views count.
    const timestamp = (value) => Number.isFinite(Number(value)) ? Number(value) : Date.parse(value) || 0;
    const recent = list(history).filter((entry) => entry && id(entry.id) && id(entry.id) !== currentId)
      .map((entry, index) => ({ entry, index, time: timestamp(entry.viewedAt) }))
      .sort((a, b) => b.time - a.time || a.index - b.index);
    let count = 0;
    for (const { entry } of recent) {
      if (seen.has(id(entry.id))) continue;
      seen.add(id(entry.id));
      const weight = 1 / (1 + count / 3);
      for (const form of canonicalForms(entry.forms)) formScores.set(form, Math.min(1, (formScores.get(form) || 0) + weight / 3));
      const color = normalize(entry.color);
      if (color) colorScores.set(color, Math.min(1, (colorScores.get(color) || 0) + weight / 3));
      count += 1;
      if (count === 20) break;
    }
    return { formScores, colorScores };
  }

  function rank(input = {}) {
    if (!input || typeof input !== 'object') return [];
    const settings = input.settings && typeof input.settings === 'object' ? input.settings : {};
    const requestedLimit = settings.limit === undefined ? 6 : Number(settings.limit);
    const limit = Number.isFinite(requestedLimit) ? Math.max(0, Math.min(50, Math.floor(requestedLimit))) : 6;
    if (!limit) return [];
    const current = input.currentProduct || {};
    const currentId = id(current.id);
    const currentForms = forms(current);
    const currentColor = normalize(input.currentVariant?.color);
    const cartIds = ids(input.cartIds);
    // Canonical family keys are product IDs. Seed cart IDs themselves so an
    // available counterpart stays excluded when the cart item is absent from
    // a partial/manual catalog; callers can also supply known family mappings.
    const excludedFamilies = new Set([
      ...(currentId ? [family(current)] : []),
      ...[...cartIds, ...ids(input.cartFamilyKeys)].map(normalize),
    ]);
    const catalog = new Map();
    for (const product of list(input.products)) {
      if (product && typeof product === 'object' && id(product.id) && !catalog.has(id(product.id))) catalog.set(id(product.id), product);
    }
    for (const product of catalog.values()) {
      if (cartIds.has(id(product.id)) || id(product.id) === currentId) excludedFamilies.add(family(product));
    }

    const positions = (values) => {
      const result = new Map();
      list(values).forEach((value, index) => { if (id(value) && !result.has(id(value))) result.set(id(value), index); });
      return result;
    };
    const manual = positions(input.manualIds);
    const featured = positions(input.featuredIds);
    const shopify = positions(input.shopifyIds);
    const preferred = ids(input.preferredIds);
    const overlap = ids(input.sameColorIds);
    const targets = new Set();
    for (const source of currentForms) {
      const overrides = list(input.rules).filter((rule) => rule && canonicalForms([rule.source]).includes(source));
      if (overrides.length) {
        if (!overrides.some((rule) => Array.isArray(rule.targets))) (defaults[source] || []).forEach((target) => targets.add(target));
        for (const rule of overrides) {
          canonicalForms(rule.targets).forEach((target) => targets.add(target));
          // These are product IDs resolved from the merchant's collection.
          ids(rule.collectionIds).forEach((productId) => preferred.add(productId));
        }
      } else (defaults[source] || []).forEach((target) => targets.add(target));
    }
    const history = affinities(settings.useHistory === false ? [] : input.history, currentId);
    const weights = settings.strategy === 'similar' ? { similar: 70, pairing: 25 } :
      settings.strategy === 'pairings' ? { similar: 22, pairing: 70 } : { similar: 48, pairing: 45 };
    const avoidSameColor = settings.avoidSameColor !== false;
    const candidates = [];
    let order = 0;
    for (const product of catalog.values()) {
      const productId = id(product.id);
      if (productId === currentId || cartIds.has(productId) || excludedFamilies.has(family(product))) continue;
      const variants = eligibleVariants(product, settings);
      if (!variants.length) continue;
      const productForms = forms(product);
      const similar = productForms.some((form) => currentForms.includes(form));
      const pairing = productForms.some((form) => targets.has(form) && !currentForms.includes(form));
      const target = productForms.some((form) => targets.has(form));
      const formAffinity = Math.max(0, ...productForms.map((form) => history.formScores.get(form) || 0));
      const variantScore = (variant) => {
        const color = normalize(variant.color);
        return (history.colorScores.get(color) || 0) * 6 + (currentColor && color === currentColor ? (avoidSameColor ? -2 : 4) : 0);
      };
      const variant = variants.reduce((best, candidate) => variantScore(candidate) > variantScore(best) ? candidate : best);
      const colorAffinity = history.colorScores.get(normalize(variant.color)) || 0;
      const isPreferred = preferred.has(productId);
      const relevant = similar || target || isPreferred || formAffinity > 0 || colorAffinity > 0;
      const score = (similar ? weights.similar : 0) + (pairing ? weights.pairing : 0) +
        (isPreferred ? 85 : 0) + formAffinity * 8 + colorAffinity * 4 +
        (shopify.has(productId) ? 6 / (1 + shopify.get(productId) / 3) : 0);
      // Pins express an explicit merchant choice and override soft overlap
      // avoidance. All automatic overlap candidates become last-resort fill.
      const tier = manual.has(productId) ? 0 : featured.has(productId) ? 1 : avoidSameColor && overlap.has(productId) ? 5 : relevant ? 2 : shopify.has(productId) ? 3 : 4;
      const position = tier === 0 ? manual.get(productId) : tier === 1 ? featured.get(productId) : tier === 3 ? shopify.get(productId) : 0;
      candidates.push({ product, variant, tier, position, score, order: order++ });
    }
    candidates.sort((a, b) => a.tier - b.tier || a.position - b.position ||
      ((a.tier === 2 || a.tier === 5) ? b.score - a.score : 0) || a.order - b.order);
    const seenFamilies = new Set();
    const results = [];
    for (const candidate of candidates) {
      const key = family(candidate.product);
      if (seenFamilies.has(key)) continue;
      seenFamilies.add(key);
      results.push({ product: candidate.product, variant: candidate.variant });
      if (results.length === limit) break;
    }
    return results;
  }

  window.WolfRecommendationRanking = Object.freeze({ forms, rank });
})();
